import { type FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/buttons';
import { IconComponent } from '@/components/IconComponent';
import { ImageComponent } from '@/components/Image';
import { Modals } from '@/helpers/modals';
import { useToggle } from '@/hooks/common';
import { useNavigation } from '@/hooks/navigation';
import { useQuestPromo } from '@/modules/promotions';
import { useGetCommonStore, useModalsStore } from '@/store';
import { ScFortuneWheelPromoSection } from '../FortuneWheelPromoSection/styled';

export interface QuestPromoSectionProps {
  onLinkClick?: () => void;
}

export const QuestPromoSection: FC<QuestPromoSectionProps> = ({
  onLinkClick,
}) => {
  const { navigate } = useNavigation();
  const { t } = useTranslation('quest');

  const [isFullInfoOpen, toggleFullInfo] = useToggle();

  const useCommonStore = useGetCommonStore();
  const isAuthenticated = useCommonStore.use.isAuthenticated();
  const open = useModalsStore.use.open();

  const { todayMissionIndex, isTodayComplete, isVipOrBonusHunter } =
    useQuestPromo();

  const translationKey = `dashboard.${isVipOrBonusHunter ? 'vip' : 'casual'}.${isTodayComplete ? 'complete' : 'active'}.${todayMissionIndex}`;

  const handleNavigateToQuest = useCallback(() => {
    navigate('/quest');
  }, [navigate]);

  const levelInfo = useMemo(() => {
    if (!isAuthenticated) {
      return (
        <>
          <div className="unauth-text">{t('bonanzaPromo.loginToJoin')}</div>
          <div className="addons">
            <ImageComponent
              alt="character"
              height={44}
              src="/quest-xmas/menu-character.webp"
              width={44}
            />
            <IconComponent name="whiteArrowRight" size="18px" />
          </div>
        </>
      );
    }

    return (
      <>
        <div className="text">
          <span>
            <b className="reward">{t(`${translationKey}.title`)}</b>
          </span>
          <span>{t(`${translationKey}.body`)}</span>
        </div>
        <div className="addons">
          <ImageComponent
            alt="character"
            height={44}
            src="/quest-xmas/menu-full.webp"
            width={44}
          />
          <IconComponent name="whiteArrowRight" size="18px" />
        </div>
      </>
    );
  }, [isAuthenticated, t, translationKey]);

  return (
    <ScFortuneWheelPromoSection
      onClick={toggleFullInfo}
      showFullInfo={isFullInfoOpen}
    >
      <div className="info">
        <div className="level-info">{levelInfo}</div>
      </div>
      {isFullInfoOpen && (
        <Button
          className="loginBtn"
          navigation
          onClick={() => {
            onLinkClick && onLinkClick();
            isAuthenticated ? handleNavigateToQuest() : open(Modals.Login);
          }}
        >
          {t(isAuthenticated ? 'pageTitle' : 'button.logIn')}
        </Button>
      )}
    </ScFortuneWheelPromoSection>
  );
};
